// src/view/Result.js
import React, { useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, Grid, Tooltip, } from '@mui/material';
import { styled } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { SectionTitle, SectionBody, Plain } from './StyledViews';
import { ruleToApplyAtom, stockDataAtom } from '../value/AppModel';
import { useAtom } from 'jotai';
import Simulate from '../util/Simulate';
import MiscValue from '../value/MiscValue';
import LightweightChart from './LightweightChart';
import { AppStyle } from '../value/AppConst';
import { useSnackbar } from 'notistack';

const ResultTitle = styled(Box)({
    backgroundColor: AppStyle.SuccessRGB, padding: 12, textAlign:'center', color: '#fff', borderRadius: 4
});

const LG = styled(Grid)({fontSize:'0.85rem', color:'#666'}); // label-grid

const DealGrid = styled(Grid)({
    borderBottom: '1px solid ' + AppStyle.InfoRGB, padding: '8px 0'
});

export default function Result() {
    const [stockRecords] = useAtom(stockDataAtom);  // array of {date, open, close, low, high, volume}
    const [ruleToApply] = useAtom(ruleToApplyAtom);
    const [result, setResult] = useState([]); // deal result. array of {date, index, price, close, amount, count}
    const [summary, setSummary] = useState({
        price: 0,
        count: 0,
        amount: 0,
        profit: 0,
        amountBought: 0, // 총 매수 금액
        amountSold: 0, // 총 매도 금액
        deposit: 0, // 예수금.
        moneyAdded: 0, // 추가 입금.
        profitPercent: 0.0, // 수익률.
        closePriceFirstDay: 0, // 첫날 종가.
        closePriceLastDay: 0, // 마지막 날 종가.
        lastDate: '', // 최종 매도한 날짜.
        indexStartDate: 0,
        indexEndDate: 0
    });
    const [history, setHistory] = useState({});
    const [daily, setDaily] = useState([]);
    const { enqueueSnackbar } = useSnackbar();

    // Boolean(ruleToApply) 이면 시뮬레이션을 시작. (useEffect)
    // stockRecords를 카피해서 rollingHigh 값을 만들어 넣고 별도의 내부배열로 만든다.
    // 그 배열을 사용해서 시뮬레이션을 실행하고 결과는 result 배열로 만든다.

    useEffect(() => {
        if(ruleToApply && stockRecords.length > 0 ) {
            const simulator = new Simulate(ruleToApply, stockRecords);
            const {result, summary} = simulator.run();
            // check
            let buyCount=0, sellCount=0;
            result.forEach((rec) => {
                if(rec.count < 0) sellCount++;
                else buyCount++;
            });
            const history = {
                tradeCount: result.length,
                buyCount,
                sellCount
            }
            /* result.forEach((rec) => {
                const act = rec.count < 0 ? '매도' : '매수';
                console.log(`${act} ${Math.abs(rec.count)} 주 (${rec.date})`);
            }); */
            // console.log('Summary: ', summary);
            const endIndex = summary.indexEndDate + 10 < stockRecords.length ? summary.indexEndDate + 10 : stockRecords.length - 1;
            const daily = []; // copy of stockRecords with action marks in ascending order
            for(let i=summary.indexStartDate; i<=endIndex; i++) {
                const rec = {...stockRecords[i]};
                daily.push(rec);
            };
            setResult(result);
            setSummary(summary);
            setHistory(history);
            setDaily(daily);
        }
    }, [ruleToApply, stockRecords]);

    const onClickCopyResultAsTsv = () => {
        const tsvData = result.map((rec) => {
            const price = MiscValue.formatFloat(rec.price);
            const amount = MiscValue.formatFloat(Math.abs(rec.amount));
            const priceAverage = MiscValue.formatFloat(Math.round(rec.priceAverage*100)/100);
            const profit = rec.profit ? MiscValue.formatFloat(Math.round(rec.profit*100)/100) : '';

            return `${rec.date}\t${rec.count < 0 ? '매도' : '매수'}\t${price}\t${Math.abs(rec.count)}\t${amount}\t${priceAverage}\t${profit}`;
        }).join('\n');
        navigator.clipboard.writeText(
            "날짜\t거래\t거래가격(중간가)\t거래수량\t거래금액\t평균단가\t수익\n"
            +
            tsvData
        );
        enqueueSnackbar("이력 데이터가 복사되었습니다. 구글 시트나 엑셀에 붙여넣기 하세요.", { variant: 'success', autoHideDuration: 1500 });
    };

    const labelAndMoney = (label, value) => {
        return(
            <><LG size={5}>{label}</LG><Grid size={7}>{MiscValue.formatFloat(value)}</Grid></>
        );
    }

    const renderResult = () => {
        if(summary.indexEndDate > 0 && result.length > 0) {
            return(
                <Box>
                    <ResultTitle>
                        매수, 매도 이력
                    </ResultTitle>
                    <Box style={{textAlign:'right', marginTop:8, marginBottom:8}}>
                        <Tooltip title="거래 이력을 탭으로 구분된 텍스트 형식으로 클립보드에 복사합니다. 구글 시트나 엑셀에 붙여넣기 하세요.">
                            <Button
                                startIcon={<ContentCopyIcon />}
                                onClick={onClickCopyResultAsTsv}
                                variant={AppStyle.mui.contained}
                                color={AppStyle.mui.success}
                            >
                                이력 복사하기
                            </Button>
                        </Tooltip>
                    </Box>
                    <Grid container spacing={1}>
                        <DealGrid size={2} style={{textAlign: 'center'}}>날짜</DealGrid>
                        <DealGrid size={1} style={{textAlign: 'center'}}>거래</DealGrid>
                        <DealGrid size={2} style={{textAlign: 'right'}}>거래가격(중간가)</DealGrid>
                        <DealGrid size={1} style={{textAlign: 'center'}}>거래수량</DealGrid>
                        <DealGrid size={1} style={{textAlign: 'center'}}>보유수량</DealGrid>
                        <DealGrid size={2} style={{textAlign: 'right'}}>거래금액</DealGrid>
                        <DealGrid size={1} style={{textAlign: 'right'}}>평균단가</DealGrid>
                        <DealGrid size={2} style={{textAlign: 'right'}}>수익</DealGrid>
                        {
                            result.map((rec) => {
                                const act = rec.count < 0 ? '매도' : '매수';
                                const cnt = Math.abs(rec.count);
                                const amount = Math.abs(rec.amount);

                                return(
                                    <React.Fragment key={rec.date}>
                                        <DealGrid size={2} style={{textAlign: 'center'}}>{rec.date}</DealGrid>
                                        <DealGrid size={1} style={{textAlign: 'center'}}>{act}</DealGrid>
                                        <DealGrid size={2} style={{textAlign: 'right'}}>{MiscValue.formatFloat(rec.price)}</DealGrid>
                                        <DealGrid size={1} style={{textAlign: 'center'}}>{MiscValue.formatInt(cnt)}</DealGrid>
                                        <DealGrid size={1} style={{textAlign: 'center'}}>{MiscValue.formatInt(rec.sum)}</DealGrid>
                                        <DealGrid size={2} style={{textAlign: 'right'}}>{MiscValue.formatFloat(amount)}</DealGrid>
                                        <DealGrid size={1} style={{textAlign: 'right'}}>{
                                            MiscValue.formatFloat(Math.round(rec.priceAverage*100)/100)
                                        }</DealGrid>
                                        <DealGrid size={2} style={{textAlign: 'right'}}>{
                                            rec.profit &&
                                            MiscValue.formatFloat(Math.round(rec.profit*100)/100)
                                            }</DealGrid>
                                    </React.Fragment>
                                );
                            })
                        }
                    </Grid>
                </Box>
            );
        }
        return null;
    };


    if(!ruleToApply) {
        return(
            <Box>
                <SectionTitle label="시뮬레이션 결과" disabled={true}/>
                <SectionBody style={{padding: '64px 0', textAlign: 'center', border: '1px solid #666'}}>
                    규칙을 선택하고 시뮬레이션을 실행하세요.
                </SectionBody>
            </Box>
        );
    }

    return (
        <Box>
            <SectionTitle label="시뮬레이션 결과" disabled={!ruleToApply}/>
            <SectionBody>
                {
                    summary.error ?
                    <p style={{color: 'red', fontSize: '1.05rem'}}>{summary.error}</p>
                    :
                    <Box style={{marginTop: 10, display: 'flex'}}>
                        <Box style={{flexGrow:1, marginRight: 3}}>
                            <Card>
                                <CardContent>
                                    <Grid container spacing={1}>
                                        {labelAndMoney('총 입금액', summary.moneyAdded)}
                                        {labelAndMoney('총 매수 금액', summary.amountBought)}
                                        {labelAndMoney('총 매도 금액', summary.amountSold)}
                                        {labelAndMoney('예수금', summary.deposit)}
                                        {labelAndMoney('총 수익', Math.round(100*summary.profit)/100)}
                                        <LG size={5}>수익률</LG>
                                        <Grid size={7}>{
                                            summary.profitPercent !== undefined ?
                                            summary.profitPercent.toFixed(2) + '%'
                                            :
                                            'N/A'
                                        }
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Box>
                        <Box style={{flexGrow:1, marginLeft: 3}}>
                            <Card>
                                <CardContent>
                                    <Grid container spacing={1}>
                                        <LG size={5}>첫 날</LG><Grid size={7}>{ruleToApply.startDate}</Grid>
                                        {labelAndMoney('첫 날 종가', summary.closePriceFirstDay)}
                                        <LG size={5}>최종 매도일</LG><Grid size={7}>{summary.lastDate}</Grid>
                                        {labelAndMoney('최종 매도일 종가', summary.closePriceLastDay)}
                                        <LG size={5}>첫날 대비 마지막 날 종가비율</LG>
                                        <Grid size={7}>{
                                            summary.closePriceFirstDay ?
                                            (summary.closePriceLastDay / summary.closePriceFirstDay * 100).toFixed(2) + '%'
                                            :
                                            'N/A'
                                        }
                                        </Grid>
                                        <LG size={5}>거래 횟수</LG>
                                        <Grid size={7}>
                                            {history.tradeCount}회 (매수 {history.buyCount}회, 매도 {history.sellCount}회)
                                        </Grid>
                                    </Grid>

                                </CardContent>
                            </Card>
                        </Box>
                    </Box>
                }
                <Box style={{marginTop: 32, marginBottom: 32}}>
                    <ResultTitle>
                        시뮬레이션 기간 중 일봉, 평균단가 변화 및 매수, 매도 이력
                    </ResultTitle>
                    <Plain>
                        평균단가: 빨간 점선 &nbsp;&nbsp;|&nbsp;&nbsp;
                        마우스 휠로 확대, 축소 &nbsp;&nbsp;|&nbsp;&nbsp;
                        마우스로 잡아 끌어 전후 이동
                    </Plain>
                    {
                        summary.indexEndDate > 0 && result.length > 0 &&
                        <LightweightChart daily={daily} result={result} />
                    }
                </Box>
                {renderResult()}
            </SectionBody>
        </Box>
    );
}
